import type { ReactNode } from "react";
import { LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { SITE } from "@/lib/site";

export type AdminTab = "enquiries" | "portfolio" | "ads";

const TABS: { id: AdminTab; label: string; short: string }[] = [
  { id: "enquiries", label: "Enquiries", short: "Enq." },
  { id: "portfolio", label: "Portfolio", short: "Art" },
  { id: "ads", label: "Ads", short: "Ads" },
];

type AdminChromeProps = {
  title: string;
  tab: AdminTab;
  onTab: (tab: AdminTab) => void;
  /** Panel-specific controls, rendered opposite the tab bar. */
  actions?: ReactNode;
  onSignOut: () => void;
};

/**
 * The admin header, shared by every tab.
 *
 * Extracted so the tab bar and sign-out cannot drift between the enquiries and
 * content panels. Tabs are plain links to `#` because this is a single-page
 * surface — the URL does not change, so `role="tablist"` is the honest
 * labelling rather than a nav of real destinations.
 */
export function AdminChrome({ title, tab, onTab, actions, onSignOut }: AdminChromeProps) {
  return (
    <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 px-5 py-4 sm:px-8">
        <div className="mr-auto">
          <div className="font-sans text-[0.6rem] tracking-[0.3em] text-gold uppercase">
            {SITE.tagline}
          </div>
          <h1 className="font-display text-2xl text-foreground">{title}</h1>
        </div>

        {actions}

        <button
          type="button"
          onClick={onSignOut}
          className="inline-flex cursor-pointer items-center gap-2 border border-border px-3 py-2 font-sans text-[0.6rem] tracking-[0.16em] text-muted-foreground uppercase transition-colors hover:border-gold hover:text-gold"
        >
          <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>

      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div role="tablist" aria-label="Admin sections" className="flex gap-1 overflow-x-auto">
          {TABS.map(item => {
            const selected = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => onTab(item.id)}
                className={cn(
                  "shrink-0 cursor-pointer border-b-2 px-3.5 py-2.5 font-sans text-[0.6rem] tracking-[0.16em] uppercase transition-colors duration-300",
                  selected
                    ? "border-gold text-gold"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                <span className="hidden sm:inline">{item.label}</span>
                <span className="sm:hidden">{item.short}</span>
              </button>
            );
          })}
        </div>
      </div>
    </header>
  );
}
